import React from 'react';
import { graphqlClient } from 'bk-web-app';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  SortingState,
  getSortedRowModel,
} from '@tanstack/react-table';
import { getHeaderElement } from '../DataEntryTableHeaderCells';
import {
  DataEntryHeaderRowLayout,
  DataEntryRowLayout,
  Table,
  TableWrapper,
  DataEntryHeaderCellLayout,
  DataEntryTableBottomGradient,
  DataEntryTableVirtuoso,
} from './DataEntryTable.styles';
import { getColumnDefinitions } from '../DataEntryTableColumnDefinitions';
import { useGeneralLedger } from '../../hooks/useGeneralLedger';
import { TableVirtuosoHandle } from 'react-virtuoso';
import { GET_DATA_ENTRIES_PAGE } from 'general-ledger/fast-gl/graphql/queries';
import {
  DataEntriesPagination,
  DataEntry,
  GetDataEntriesPage,
  LedgerAccount,
} from 'general-ledger/fast-gl/types';
import useV2Query from 'utils/useV2Query';
import { LedgerAccountHeader } from '../ledger-account-header/LedgerAccountHeader';
import { LedgerRowSkeleton } from '../ledger-table/LedgerTableSkeleton';
import { Skeleton } from '../skeleton/Skeleton';
import { useClient } from 'bk-web-app/ClientContext';
import { ErrorMessage } from 'client-web-app/components';
import {
  getMultiSort,
  getMoneyInTransitSort,
  defaultMultiSort,
  defaultMoneyInTransitSort,
} from '../../utils/sorting';

type DataEntryTableProps = {
  ledgerAccount: LedgerAccount;
};

const MAX_RETRIES = 3;
const AVERAGE_ROW_HEIGHT = 45;
const AVERAGE_ROWS_INITIALLY_DISPLAYED = 13;
const HEADER_HEIGHT = 120;

const DataEntryTable = ({ ledgerAccount }: DataEntryTableProps): React.ReactElement | null => {
  const isAccount = !!ledgerAccount.role;
  const isQualityIssues = ledgerAccount.name === 'Quality Issues Summary';
  const tableRef = React.useRef<TableVirtuosoHandle>(null);
  const tableElementRef = React.useRef<HTMLTableElement>(null);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const [reachedBottom, setReachedBottom] = React.useState<boolean>(false);
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const { search, dateRange, updateSelectedJournalEntry } = useGeneralLedger();
  const client = useClient();
  const is1099Enabled = client?.featureFlags.report1099;
  const [retries, setRetries] = React.useState(0);
  const columns = React.useMemo<ColumnDef<DataEntry>[]>(
    () => getColumnDefinitions(search, isAccount, isQualityIssues),
    [search, isAccount, isQualityIssues],
  );

  const total = ledgerAccount.dataEntries?.pageInfo.total || 0;

  const { data, loading, error, refetch, fetchMore } = useV2Query<GetDataEntriesPage>(
    GET_DATA_ENTRIES_PAGE,
    {
      variables: {
        clientId: client.id,
        ledgerId: ledgerAccount.id,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        query: search,
        multiSort: defaultMultiSort,
        moneyInTransitSort: defaultMoneyInTransitSort,
      },
      skip: ledgerAccount.dataEntries?.dataEntries.length === total,
      notifyOnNetworkStatusChange: true,
      onError: () => {
        if (retries <= MAX_RETRIES) {
          setRetries(retries + 1);
          refetch();
        }
      },
      onCompleted: () => {
        !hasLoaded && setHasLoaded(true);
        retries > 0 && setRetries(0);
      },
    },
  );

  const entries = React.useMemo(() => {
    return data?.ledgerAccount.dataEntries?.dataEntries.length
      ? data?.ledgerAccount.dataEntries.dataEntries
      : ledgerAccount.dataEntries?.dataEntries
      ? ledgerAccount.dataEntries.dataEntries
      : [];
  }, [data, ledgerAccount.dataEntries]);

  React.useEffect(() => {
    if (!data?.ledgerAccount?.dataEntries || loading) return;
    if (data.ledgerAccount.dataEntries?.pageInfo.hasNextPage) {
      const multiSort = getMultiSort(sorting);
      const moneyInTransitSort = getMoneyInTransitSort(sorting);

      const cacheId = graphqlClient.cache.identify({
        id: data.ledgerAccount.id,
        __typename: 'LedgerAccount',
      });
      // We're clearing the cached data entries here on a sort change so that we can repaginate in the new order effectively through the entries.
      // This could potentially be improved by keeping the cached entries, and working out some logic about when to include them when the endCursor
      // matches one of those entries. But this works well for now.
      graphqlClient.cache.modify({
        id: cacheId,
        fields: {
          dataEntries(cachedDe: DataEntriesPagination) {
            return { pageInfo: cachedDe.pageInfo, dataEntries: [] };
          },
        },
        broadcast: false,
      });
      refetch({
        ledgerId: data.ledgerAccount.id,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        query: search,
        after: null,
        multiSort,
        moneyInTransitSort,
      });
      tableRef.current?.scrollToIndex(0);
    }
  }, [sorting]);

  const table = useReactTable({
    data: entries || [],
    columns,
    initialState: {
      columnVisibility: {
        contraLedger: isAccount ? true : false,
        source1099: is1099Enabled,
        contractor: is1099Enabled,
      },
    },
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const onReachedEnd = React.useCallback(() => {
    if (data?.ledgerAccount.dataEntries?.pageInfo.hasNextPage) {
      fetchMore({
        variables: {
          after: data?.ledgerAccount.dataEntries.pageInfo.endCursor,
        },
      });
    }
  }, [
    data?.ledgerAccount.dataEntries?.pageInfo.hasNextPage,
    data?.ledgerAccount.dataEntries?.pageInfo.endCursor,
    fetchMore,
  ]);

  const fetchMoreOnBottomReached = () => {
    onReachedEnd();
  };

  const getContainerHeight = React.useCallback(() => {
    const guessedHeight =
      entries.length * AVERAGE_ROW_HEIGHT + (isQualityIssues ? 0 : HEADER_HEIGHT);
    if (
      tableElementRef?.current?.offsetHeight &&
      tableElementRef?.current?.offsetHeight > guessedHeight
    )
      return `${tableElementRef?.current?.offsetHeight}px`;

    return `${guessedHeight}px`;
  }, [entries.length]);

  const headers = table.getHeaderGroups() || [];
  const rows = table.getRowModel()?.rows || [];

  // Show a retry message if the initial load fails. This can happen if the user is scrolling quickly.
  // If we've already loaded data entries, and get a pagination error, we don't want to hide the whole table.
  if (error && !data?.ledgerAccount) {
    return (
      <ErrorMessage
        message={`
        There was an error loading data for this ledger.
        ${retries <= MAX_RETRIES ? 'Retrying...' : 'Please refresh the page.'}
        `}
      />
    );
  }

  if (!hasLoaded && loading && !entries.length) {
    return (
      <LedgerRowSkeleton
        tableRows={
          !total || total > AVERAGE_ROWS_INITIALLY_DISPLAYED
            ? AVERAGE_ROWS_INITIALLY_DISPLAYED
            : total
        }
      />
    );
  }

  return (
    <DataEntryTableVirtuoso
      ref={tableRef}
      style={{
        maxHeight: '80vh',
        height: getContainerHeight(),
        width: 'calc(100% - 64px)',
      }}
      data={entries || []}
      defaultItemHeight={AVERAGE_ROW_HEIGHT}
      endReached={fetchMoreOnBottomReached}
      increaseViewportBy={600} // Too much overscan here leads to degraded performance.
      components={{
        Table: props => (
          <TableWrapper>
            <Table {...props} ref={tableElementRef} />
          </TableWrapper>
        ),
        TableRow: props => {
          // eslint-disable-next-line
          const index = props['data-index'];
          const row = rows[index];
          return (
            <DataEntryRowLayout
              {...props}
              hasBackgroundColor={index % 2 === 0}
              key={row.id}
              onMouseUp={e => {
                // we need to check first if this click event is being caused the AA attempting to highlight text
                // if nothing is selected, the sidebar will open as usual
                e.preventDefault();
                const selection = window.getSelection()?.toString();
                if (!selection || selection.length <= 0)
                  updateSelectedJournalEntry &&
                    updateSelectedJournalEntry(row.original.journalEntry);
              }}
              // when there is a selection, clicking directly on it will not open the row or remove the selection
              // adding a double click action helps make the ui feel less glitchy when a selection exists
              onDoubleClick={() =>
                updateSelectedJournalEntry && updateSelectedJournalEntry(row.original.journalEntry)
              }
            />
          );
        },
      }}
      atBottomStateChange={setReachedBottom}
      atBottomThreshold={AVERAGE_ROW_HEIGHT}
      fixedFooterContent={() => {
        if (entries.length <= 15 || reachedBottom) return null;
        return (
          <tr>
            <td colSpan={100}>
              <DataEntryTableBottomGradient />
            </td>
          </tr>
        );
      }}
      fixedHeaderContent={() => {
        return headers.map(headerGroup => (
          <React.Fragment key={headerGroup.id}>
            {ledgerAccount.name != 'Quality Issues Summary' && (
              <tr>
                <th colSpan={100}>
                  <LedgerAccountHeader ledgerAccount={ledgerAccount} />
                </th>
              </tr>
            )}
            <DataEntryHeaderRowLayout key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                return (
                  <DataEntryHeaderCellLayout
                    key={header.id}
                    width={header.getSize()}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {!entries.length ? null : (
                      <>
                        {getHeaderElement(
                          header.column.columnDef.header as string,
                          entries,
                          ledgerAccount.dataEntries?.pageInfo.total,
                          ledgerAccount.id,
                          header.column.getCanSort(),
                          header.column.getIsSorted(),
                        )}
                      </>
                    )}
                  </DataEntryHeaderCellLayout>
                );
              })}
            </DataEntryHeaderRowLayout>
            {loading ? (
              <tr>
                <th colSpan={100}>
                  <Skeleton height={24} />
                </th>
              </tr>
            ) : null}
          </React.Fragment>
        ));
      }}
      itemContent={index => {
        const row = rows[index];
        return row
          .getVisibleCells()
          .map(cell => (
            <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
          ));
      }}
    />
  );
};

export default React.memo(DataEntryTable);
